import { api, apiFetch } from '@/api/client';
import { ApiError } from '@/api/errors';

/** Limosa badge for an installer on the task date (see API utils/limosa-status.ts). */
export type LimosaBadge = 'groen' | 'oranje' | 'rood' | 'te_controleren' | 'niet_nodig';
export type LimosaRecordStatus = 'te_controleren' | 'goedgekeurd' | 'afgekeurd';

/** Why "Limosa verplicht" is what it is (decision 16). */
export interface LimosaVerplichtBron {
  verplicht: boolean;
  /** manueel = administratie chose; otherwise the company's country decided. */
  bron: 'manueel' | 'buitenland' | 'belgie' | 'onbekend';
  land: string | null;
  keuze: 'ja' | 'nee' | null;
}

/** A worker of a subcontractor on a day task (decisions 12–15). */
export interface WorkerRow {
  id: number;
  name: string;
  aanwezig: boolean | null;
  ingecheckt: boolean | null;
  limosa: { badge: LimosaBadge; geldigTot: string | null } | null;
  /** This person's "Limosa verplicht" and where it comes from (decision 17). */
  verplicht?: LimosaVerplichtBron | null;
}

/** One task × installer. A null field does not exist in this Odoo database yet ("onbekend"). */
export interface AdministratieRow {
  key: string;
  taskId: number;
  taskName: string;
  projectId: number | null;
  projectName: string;
  klant: string | null;
  werfAdres: string | null;
  installerId: number | null;
  installerName: string | null;
  checkinWorkNr: string | null;
  /** Projectleider name; the werf header takes the planning's colour for it. */
  projectleider: string | null;
  /** The task has a sale order, so its Checkin@work number can be set. */
  hasSaleOrder: boolean;
  /** The task's day, YYYY-MM-DD. */
  taskDate: string;
  /** Worker rows for a subcontractor with worker contacts; null for one person or a team. */
  workers: WorkerRow[] | null;
  /** At least one worker is marked present. */
  presenceReported: boolean;
  gemeld: boolean | null;
  ingecheckt: boolean | null;
  limosaVerplicht: boolean | null;
  limosaVerplichtBron: LimosaVerplichtBron | null;
  limosa: { badge: LimosaBadge; geldigTot: string | null } | null;
}

export interface AdministratieDay {
  date: string;
  rows: AdministratieRow[];
  /** The day hit the 100-task cap; some tasks are not shown. */
  truncated: boolean;
}

export interface LimosaPerson {
  id: number;
  name: string;
  isSelf: boolean;
}

export interface LimosaDetail {
  id: number;
  partnerId: number;
  partnerName: string;
  geldigVan: string;
  geldigTot: string;
  status: LimosaRecordStatus;
  limosaNummer: string | null;
  hasDocument: boolean;
  uploadedBy: string | null;
  uploadedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  /** Approved by the server's PDF check, not yet by a person (absent from an older API). */
  autoApproved?: boolean;
  opmerking: string | null;
  /** The projects it was uploaded for. */
  projectIds: number[];
}

/** A project a Limosa is linked to. */
export interface LimosaProject {
  id: number;
  name: string;
}

export function fetchAdministratieDay(date: string): Promise<AdministratieDay> {
  return api<AdministratieDay>(`/api/administratie/tasks?date=${date}`);
}

/** Check in or out: the installer itself, or (`personId`) one of its workers. */
export function setIngecheckt(taskId: number, installerId: number, value: boolean, personId?: number): Promise<unknown> {
  return api(`/api/administratie/tasks/${taskId}/ingecheckt`, {
    method: 'PATCH',
    body: JSON.stringify(personId != null ? { installerId, personId, value } : { installerId, value }),
  });
}

/** Administratie marks a worker of an installer present or absent on a day task. */
export function setAanwezig(taskId: number, installerId: number, workerId: number, value: boolean): Promise<unknown> {
  return api(`/api/administratie/tasks/${taskId}/aanwezig`, {
    method: 'PATCH',
    body: JSON.stringify({ installerId, workerId, value }),
  });
}

/** Installer side: the login's workers and who is present per task on `date`. */
export interface Aanwezigheid {
  workers: Array<{ id: number; name: string }>;
  tasks: Array<{ taskId: number; present: number[] }>;
  /** False for a day ahead or more than 30 days back. */
  editable: boolean;
}

export function fetchAanwezigheid(date: string): Promise<Aanwezigheid> {
  return api(`/api/installer/aanwezigheid?date=${date}`);
}

export function setMyAanwezig(taskId: number, workerId: number, value: boolean): Promise<unknown> {
  return api(`/api/installer/tasks/${taskId}/aanwezig`, {
    method: 'PATCH',
    body: JSON.stringify({ workerId, value }),
  });
}

export function setGemeld(projectId: number, installerId: number, value: boolean): Promise<unknown> {
  return api(`/api/administratie/projects/${projectId}/gemeld`, {
    method: 'PATCH',
    body: JSON.stringify({ installerId, value }),
  });
}

/** Set or clear (empty string) the Checkin@work number; the server finds the order. */
export function setCheckinWork(projectId: number, value: string): Promise<{ projectId: number; value: string | null }> {
  return api(`/api/administratie/projects/${projectId}/checkinwork`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  });
}

/** ja / nee is an explicit choice; automatisch hands the decision back to the company's country. */
export function setLimosaVerplicht(
  partnerId: number,
  value: 'ja' | 'nee' | 'automatisch',
): Promise<{ partnerId: number; value: string; effective: LimosaVerplichtBron | null }> {
  return api(`/api/administratie/partners/${partnerId}/limosa-verplicht`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  });
}

export function fetchInstallerLimosa(partnerId: number): Promise<{
  people: LimosaPerson[];
  records: LimosaDetail[];
  /** "Limosa verplicht" per person id. */
  verplicht?: Record<string, LimosaVerplichtBron>;
  /** Names of the linked projects, by project id. */
  projects?: Record<string, string>;
}> {
  return api(`/api/administratie/limosa/${partnerId}`);
}

/** Replace the projects a Limosa is linked to. */
export function setLimosaProjects(id: number, projectIds: number[]): Promise<unknown> {
  return api(`/api/administratie/limosa/${id}/projects`, { method: 'PATCH', body: JSON.stringify({ projectIds }) });
}

/** Projects to link a Limosa to, by name, S-number or client. */
export async function searchLimosaProjects(q: string, signal?: AbortSignal): Promise<LimosaProject[]> {
  const data = await api<{ results: LimosaProject[] }>(`/api/administratie/projecten/zoek?q=${encodeURIComponent(q)}`, { signal });
  return data.results;
}

export function reviewLimosa(
  id: number,
  review: { status: 'goedgekeurd' | 'afgekeurd'; opmerking?: string; geldigVan?: string; geldigTot?: string },
): Promise<unknown> {
  return api(`/api/administratie/limosa/${id}`, { method: 'PATCH', body: JSON.stringify(review) });
}

/**
 * Open a Limosa document in a new tab. The route is authenticated (the document
 * holds a national number and never gets a public URL), so it is fetched with
 * the login headers and shown from a blob.
 */
export async function openLimosaDocument(id: number): Promise<void> {
  // Open the tab synchronously, inside the click, or a popup blocker eats it.
  const tab = window.open('', '_blank');
  try {
    const res = await apiFetch(`/api/limosa/${id}/document`);
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/** The file name from Content-Disposition: the UTF-8 form first, then the plain one. */
function dispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // fall through to the plain name
    }
  }
  return header.match(/filename="([^"]+)"/i)?.[1] ?? null;
}

/**
 * Download the documents of everyone present on one werf on `date` as one zip.
 * Fetched with the login headers (the documents hold personal data and never get
 * a public URL). A refusal, e.g. nobody marked present, throws its Dutch message.
 */
export async function downloadWerfDocumenten(projectId: number, date: string): Promise<void> {
  const res = await apiFetch(`/api/administratie/werven/${projectId}/documenten?date=${date}`);
  let blob: Blob;
  try {
    blob = await res.blob();
  } catch {
    // The server cut the zip off after it started: the browser's own text is English.
    throw new ApiError('DOWNLOAD_ABORTED', 'Download onderbroken, probeer opnieuw', res.status);
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = dispositionFileName(res.headers.get('Content-Disposition')) ?? `${date} documenten.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface MyLimosaStatus {
  partnerId: number;
  badge: Exclude<LimosaBadge, 'niet_nodig'>;
  geldigTot: string | null;
  /** This person's "Limosa verplicht" (decision 17). */
  verplicht: boolean;
}

export interface MyLimosa {
  people: LimosaPerson[];
  records: LimosaDetail[];
  status: MyLimosaStatus[];
}

/** The installer's own side: its people, their declarations and each one's status on `date`. */
export function fetchMyLimosa(date: string): Promise<MyLimosa> {
  return api(`/api/limosa/mine?date=${date}`);
}

export interface LimosaUploadInput {
  partnerId: number;
  geldigVan: string;
  geldigTot: string;
  limosaNummer?: string;
  /** Installer: the job card it uploads from; the API links that task's project. */
  taskId?: number;
  /** Administratie: the projects to link. */
  projectIds?: number[];
  file: File;
}

function limosaFormData(input: LimosaUploadInput): FormData {
  const fd = new FormData();
  // Text fields BEFORE the file: fields after the stream can be lost.
  fd.append('partnerId', String(input.partnerId));
  fd.append('geldigVan', input.geldigVan);
  fd.append('geldigTot', input.geldigTot);
  if (input.limosaNummer) fd.append('limosaNummer', input.limosaNummer);
  if (input.taskId) fd.append('taskId', String(input.taskId));
  if (input.projectIds?.length) fd.append('projectIds', input.projectIds.join(','));
  fd.append('file', input.file);
  return fd;
}

/** An installer's upload is goedgekeurd at once only when the server's PDF check matched in full. */
export function uploadLimosa(input: LimosaUploadInput): Promise<{ id: number; status: 'te_controleren' | 'goedgekeurd' }> {
  return api('/api/limosa', { method: 'POST', body: limosaFormData(input) });
}

/** What the API read from a Limosa L1 PDF (see API limosa-l1/parse.ts). Never holds the identification number. */
export interface LimosaPdfGegevens {
  periodeVan: string | null;
  periodeTot: string | null;
  declaratienummer: string | null;
  werkgever: { naam: string | null; btw: string | null };
  werknemer: { naam: string | null };
  plaatsen: string[];
}

/** Per person id: does the PDF name someone else, or another employer? */
export type LimosaPdfLezing =
  | { leesbaar: true; gegevens: LimosaPdfGegevens; afwijking: Record<string, { naam: boolean; werkgever: boolean }> }
  | { leesbaar: false };

function fileFormData(file: File): FormData {
  const fd = new FormData();
  fd.append('file', file);
  return fd;
}

/** Read a Limosa PDF for the upload form; stores nothing. */
export function readLimosaPdf(file: File): Promise<LimosaPdfLezing> {
  return api('/api/limosa/lees', { method: 'POST', body: fileFormData(file) });
}

export function readLimosaPdfAsAdministratie(installerId: number, file: File): Promise<LimosaPdfLezing> {
  return api(`/api/administratie/limosa/${installerId}/lees`, { method: 'POST', body: fileFormData(file) });
}

/** Administratie uploads a Limosa it received for one person of an installer row; stored approved. */
export function uploadLimosaAsAdministratie(installerId: number, input: LimosaUploadInput): Promise<{ id: number; status: 'goedgekeurd' }> {
  return api(`/api/administratie/limosa/${installerId}`, { method: 'POST', body: limosaFormData(input) });
}
